/* What Blueprint does to somebody's Entra account when they are wound down.
 *
 * A sibling of DirectoryClient, which only ever asks "who is in these groups" about
 * the hub's own access model. This asks about ONE person, and acts on the answer.
 *
 * AS THE SIGNED-IN ADMINISTRATOR, NEVER AS THE APP
 *
 * Every call is delegated, so it can do nothing the person pressing the button
 * could not do by hand in the Entra portal, and Conditional Access and role
 * activation (PIM) apply unchanged. There is deliberately no app-only identity
 * here: a browser app that could disable accounts in its own right is a much larger
 * thing to leave lying around than one that acts as whoever is signed in.
 *
 * THE NARROWEST PERMISSION FOR EACH THING
 *
 * Not User.ReadWrite.All, which would also let it rewrite anybody's profile and
 * reset passwords. Microsoft publishes a narrower one for each job, and each is
 * used: one that can only enable or disable an account, one that can only end
 * sessions, one that can only change licences. They are consented to the app
 * registration once, by a tenant administrator (provisioning/
 * Configure-WindDownPermissions.ps1), and the signed-in person must ALSO hold the
 * matching Entra role: a permission is necessary and not sufficient.
 *
 * WHAT WILL GO WRONG, AND WHAT IT LOOKS LIKE
 *
 * Entra's own refusal is "Insufficient privileges to complete the operation", which
 * does not say whether the app lacks the permission or the person lacks the role.
 * refusal() says which job failed and names the two things to check.
 */

import { GraphError, type TokenProvider } from '../sharepoint/GraphClient';
import type { IMembership, IWindDownFacts } from '../../lib/windDown';

const GRAPH = 'https://graph.microsoft.com/v1.0';

/** Read what is true of the person. */
export const SCOPES_READ = ['User.Read.All', 'GroupMember.Read.All'];
/** Remove them from a group. Already consented for the hub's access groups. */
export const SCOPES_GROUPS = ['GroupMember.ReadWrite.All'];
export const SCOPES_DISABLE = ['User.EnableDisableAccount.All'];
export const SCOPES_SESSIONS = ['User.RevokeSessions.All'];
export const SCOPES_LICENCES = ['LicenseAssignment.ReadWrite.All'];

interface IGroupRow {
  id: string;
  displayName?: string;
  groupTypes?: string[];
  mailEnabled?: boolean;
  securityEnabled?: boolean;
  onPremisesSyncEnabled?: boolean | null;
  isAssignableToRole?: boolean | null;
}

interface ILicenceState {
  skuId: string;
  assignedByGroup?: string | null;
}

export class OffboardClient {
  constructor(private readonly getToken: TokenProvider) {}

  private async request<T>(scopes: string[], url: string, init: RequestInit = {}): Promise<T> {
    const token = await this.getToken(scopes);
    const res = await fetch(url.startsWith('http') ? url : `${GRAPH}${url}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...init.headers,
      },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      const detail = (body as { error?: { message?: string } } | null)?.error?.message;
      throw new GraphError(
        detail ? `Graph ${res.status}: ${detail}` : `Graph ${res.status} on ${url}`,
        res.status,
        body,
      );
    }
    if (res.status === 204 || res.status === 202) return undefined as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  /** Every page of a collection. memberOf is paged at 100 by default. */
  private async all<T>(scopes: string[], url: string): Promise<T[]> {
    const out: T[] = [];
    let next: string | undefined = url;
    while (next) {
      const page: { value: T[]; '@odata.nextLink'?: string } = await this.request(scopes, next);
      out.push(...page.value);
      next = page['@odata.nextLink'];
    }
    return out;
  }

  /** Ask for every permission up front, in one request.
   *
   *  Acquiring a token for a permission nobody has consented to sends the browser
   *  away to Microsoft's consent page. That must happen BEFORE anything is changed,
   *  not halfway through a wind-down with the progress bar on screen. So the plan
   *  step calls this first: if consent is needed, they are taken there now, and
   *  come back to an untouched account. */
  async preflight(withLicences: boolean): Promise<void> {
    const wanted = [...SCOPES_READ, ...SCOPES_GROUPS, ...SCOPES_DISABLE, ...SCOPES_SESSIONS];
    if (withLicences) wanted.push(...SCOPES_LICENCES);
    await this.getToken(wanted);
  }

  /** What is true of their account right now. */
  async inspect(userId: string): Promise<IWindDownFacts> {
    /* Asked on its own, first. A 404 here is not a failure of the wind-down: it is
       the ordinary answer for somebody who left and whose account was deleted, and
       the plan has to be able to say so. Anything else is a real error and is
       thrown as it always was. */
    let user: { accountEnabled?: boolean; licenseAssignmentStates?: ILicenceState[] };
    try {
      user = await this.request(
        SCOPES_READ,
        `/users/${userId}?$select=accountEnabled,licenseAssignmentStates`,
      );
    } catch (e) {
      if (e instanceof GraphError && e.status === 404) {
        return { accountExists: false, memberships: [], signInEnabled: false, directLicences: [], groupLicenceCount: 0 };
      }
      throw e;
    }

    const [groups, roles] = await Promise.all([
      this.all<IGroupRow>(
        SCOPES_READ,
        `/users/${userId}/memberOf/microsoft.graph.group?$select=id,displayName,groupTypes,mailEnabled,securityEnabled,onPremisesSyncEnabled,isAssignableToRole&$top=999`,
      ),
      this.all<{ id: string; displayName?: string }>(
        SCOPES_READ,
        `/users/${userId}/memberOf/microsoft.graph.directoryRole?$select=id,displayName&$top=999`,
      ),
    ]);

    const memberships: IMembership[] = [
      ...groups.map((g) => ({
        id: g.id,
        type: '#microsoft.graph.group',
        name: g.displayName ?? g.id,
        groupTypes: g.groupTypes ?? [],
        mailEnabled: g.mailEnabled,
        securityEnabled: g.securityEnabled,
        onPremisesSyncEnabled: g.onPremisesSyncEnabled,
        isAssignableToRole: g.isAssignableToRole,
      })),
      ...roles.map((r) => ({ id: r.id, type: '#microsoft.graph.directoryRole', name: r.displayName ?? r.id })),
    ];

    /* A licence can be assigned by a group and directly at once. Only the direct
       assignment is the person's to remove; the other goes when the group does. */
    const states = user.licenseAssignmentStates ?? [];
    const direct = Array.from(new Set(states.filter((s) => !s.assignedByGroup).map((s) => s.skuId)));
    const viaGroup = Array.from(new Set(states.filter((s) => !!s.assignedByGroup).map((s) => s.skuId)));

    return {
      memberships,
      signInEnabled: user.accountEnabled !== false,
      directLicences: direct,
      groupLicenceCount: viaGroup.filter((id) => direct.indexOf(id) < 0).length,
    };
  }

  async blockSignIn(userId: string): Promise<'blocked'> {
    try {
      await this.request(SCOPES_DISABLE, `/users/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ accountEnabled: false }),
      });
      return 'blocked';
    } catch (e) {
      throw refusal('block their sign-in', e, 'User.EnableDisableAccount.All', 'User Administrator or Helpdesk Administrator');
    }
  }

  async revokeSessions(userId: string): Promise<void> {
    try {
      await this.request(SCOPES_SESSIONS, `/users/${userId}/revokeSignInSessions`, { method: 'POST' });
    } catch (e) {
      throw refusal('end their sessions', e, 'User.RevokeSessions.All', 'User Administrator or Authentication Administrator');
    }
  }

  async removeFromGroup(groupId: string, userId: string): Promise<void> {
    try {
      await this.request(SCOPES_GROUPS, `/groups/${groupId}/members/${userId}/$ref`, { method: 'DELETE' });
    } catch (e) {
      /* Already not a member is the outcome that was wanted. */
      if (e instanceof GraphError && e.status === 404) return;
      throw refusal('remove them from the group', e, 'GroupMember.ReadWrite.All', 'Groups Administrator, or owner of the group');
    }
  }

  async removeLicences(userId: string, skuIds: string[]): Promise<void> {
    try {
      await this.request(SCOPES_LICENCES, `/users/${userId}/assignLicense`, {
        method: 'POST',
        body: JSON.stringify({ addLicenses: [], removeLicenses: skuIds }),
      });
    } catch (e) {
      throw refusal('remove their licences', e, 'LicenseAssignment.ReadWrite.All', 'License Administrator');
    }
  }
}

/** Entra's own words do not say which side lacks what. This does. */
export function refusal(job: string, e: unknown, permission: string, role: string): Error {
  if (e instanceof GraphError && (e.status === 401 || e.status === 403)) {
    return new Error(
      `Could not ${job}: Entra refused. Check that the app has ${permission} with admin consent, `
      + `and that you hold ${role} right now (a role from PIM must be activated).`,
    );
  }
  return e instanceof Error ? e : new Error(String(e));
}
